<template>
  <div class="container">
    <main class="main-layout">
      <section class="card upload-section left-card">
        <div class="card-header">
          <h2>Camera</h2>
        </div>
        <div class="card-body">
          <form @submit.prevent="handleSubmit" class="upload-form">
            <button type="submit" class="btn btn-primary upload-btn" :disabled="isLoading">
              <span v-if="!isLoading">Start Camera & Segment</span>
              <span v-if="isLoading">
                <i class="fas fa-spinner fa-spin"></i> Running...
              </span>
            </button>
          </form>
        </div>
      </section>

      <section class="card image-compare-section right-card">
        <div class="card-header">
          <h2>Original & Segmentation</h2>
        </div>
        <div class="card-body">
          <div class="image-compare-container">
            <div class="image-item">
              <h3 class="image-title">Original</h3>
              <div class="image-box">
                <video 
                  class="display-image" 
                  ref="originalVideo"
                  autoplay
                  playsinline
                ></video>
              </div>
            </div>

            <div class="image-item">
              <h3 class="image-title">Segmentation</h3>
              <div class="image-box">
                <canvas class="display-image" ref="segCanvas"></canvas>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    
    <footer class="page-footer">
      <p> Pig Segmentation System &copy; 2025</p>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const isLoading = ref(false);
const originalVideo = ref(null);
const segCanvas = ref(null);
let stream = null;

// 打开摄像头
const startCamera = async () => {
  stream = await navigator.mediaDevices.getUserMedia({ video: true });
  originalVideo.value.srcObject = stream;
  await new Promise(resolve => originalVideo.value.onloadedmetadata = resolve);
};

const handleSubmit = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  await startCamera();

  const video = originalVideo.value;
  const canvas = segCanvas.value;
  const ctx = canvas.getContext('2d');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  // 核心：1秒执行一帧，串行等待后端返回
  const intervalTime = 1000;
  async function processFrame() {
    if (!isLoading.value) return;

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCtx.drawImage(video, 0, 0);

    const blob = await new Promise(resolve => tempCanvas.toBlob(resolve, 'image/jpeg'));
    const fd = new FormData();
    fd.append('image', blob);

    try {
      const res = await fetch('https://fengguoheng.online/segment', {
        method: 'POST',
        body: fd
      });
      const data = await res.json();

      if (data.overlay_image) {
        const img = new Image();
        await new Promise(resolve => {
          img.onload = resolve;
          img.src = 'data:image/jpeg;base64,' + data.overlay_image;
        });
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      }
    } catch (e) {}

    // 间隔 1 秒再取下一帧
    setTimeout(processFrame, intervalTime);
  }

  processFrame();
};
</script>

<style scoped>
.container {
  font-family: 'Segoe UI', Roboto, sans-serif;
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
  background: #f8fafc;
  min-height: 100vh;
}

.main-layout {
  display: flex;
  gap: 25px;
  align-items: flex-start; /* 关键：不拉伸高度，从上到下对齐 */
}

/* 左边卡片：高度由内容自适应，不要被拉太高 */
.left-card {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: auto; /* 不强制高度 */
}

.left-card .card-header {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px; /* 减小header高度 */
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.left-card .card-body {
  flex: none; /* 不自动撑高 */
  padding: 20px; /* 内边距减小 */
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-form {
  width: 100%;
}

.right-card {
  flex: 1;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  overflow: hidden;
}

.card-header h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #1e293b;
}

.upload-btn {
  padding: 12px;
  width: 100%;
  font-size: 1rem;
}

.btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.image-compare-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 25px;
  height: 100%;
}

.image-item {
  flex: 1;
  text-align: center;
}

.image-title {
  font-size: 1.2rem;
  margin-bottom: 12px;
  color: #334155;
  font-weight: 600;
}

.image-box {
  width: 100%;
  height: 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
}

.display-image {
  max-width: 100%;
  max-height: 450px;
  object-fit: contain;
  border-radius: 6px;
}

.page-footer {
  margin-top: 30px;
  text-align: center;
  color: #94a3b8;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 1024px) {
  .main-layout {
    flex-direction: column;
  }
  .left-card {
    width: 100%;
  }
}
</style>